import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, input, OnChanges, OnInit, output, SimpleChanges } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  CbAccordionComponent,
  CbAccordionContentComponent,
  CbAccordionHeaderComponent,
  CbAccordionPanelComponent,
  CbIconModule,
  CbSelectChoiceModule,
} from '@chubb/ui-components';
import { AccordionModule } from 'primeng/accordion';
import { SelectModule } from 'primeng/select';
import { Coverage } from '../../../interfaces/coverage.model';
import { Customer } from '../../../interfaces/customer.model';
import { Policy } from '../../../interfaces/policy.model';
import { PaymentFrequency } from '../../../shared/enums/payment-frequency.enum';
import { ReferenceDataSet } from '../../../shared/services/reference-data.types';

@Component({
  selector: 'app-premium-breakdown',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SelectModule,
    CbSelectChoiceModule,
    CbAccordionComponent,
    CbAccordionPanelComponent,
    CbAccordionHeaderComponent,
    CbAccordionContentComponent,
    AccordionModule,
    CbIconModule,
  ],
  templateUrl: './premium-breakdown.component.html',
  styleUrl: './premium-breakdown.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PremiumBreakdownComponent implements OnInit, OnChanges {
  coverages = input.required<Coverage[]>();
  customers = input.required<Customer[]>();
  policy = input.required<Policy>();
  paymentFrequencyOptions = input<ReferenceDataSet>([]);
  coverageCodeVals = input.required<{ code: string; val: string }[]>();
  paymentFrequencyLabel = input<string>('');
  chosenFrequency = output<string>();

  paymentFrequencyControl = new FormControl();
  openStates: boolean[] = [];

  ngOnInit() {
    this.intitializePaymentFrequency();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ngOnChanges(changes: SimpleChanges): void {
    this.intitializePaymentFrequency();
  }

  /**
   * Intitialises the payment frequency form control based on the current policy's payment frequency.
   * Sets the control's value if available and subscribes to changes to update the payment frequency label.
   *
   * @returns {void}
   */
  intitializePaymentFrequency() {
    if (this.policy().paymentFrequency) {
      this.paymentFrequencyControl.setValue(this.policy().paymentFrequency);
      this.updatePaymentFrequencyLabel();
    }
    this.paymentFrequencyControl?.valueChanges.subscribe(() => {
      this.updatePaymentFrequencyLabel();
    });
  }

  updatePaymentFrequencyLabel() {
    this.chosenFrequency.emit(this.paymentFrequencyControl.value);
  }

  // Get full name of customer
  getCustomerFullName(customer: Customer): string {
    return `${customer.firstName} ${customer.lastName}`;
  }

  // Get coverages for a customer
  getCoveragesForCustomer(customer: Customer): Coverage[] {
    return this.coverages()
      .filter((c: Coverage) => c.customerNumber === customer.number)
      .map((c: Coverage) => ({
        code: c.code,
        name: c.name,
        sumInsured: c.sumInsured,
        premium: c.premium,
        premiumCurrent: c.premiumCurrent,
        premiumWeekly: c.premiumWeekly,
      }));
  }

  getTotalPremium(customer: Customer): number {
    return this.getCoveragesForCustomer(customer).reduce(
      (total: number, c: Coverage) => total + (c.premiumCurrent ?? 0),
      0,
    );
  }

  getGrandTotalPremium(): number {
    // Sum premiums of all customers
    const totalCustomerPremiums = (this.customers() || []).reduce(
      (total: number, customer: Customer) => total + this.getTotalPremium(customer),
      0,
    );
    // Add policy fee if available
    const fee = this.policy().fee?.amount ?? 0;
    return totalCustomerPremiums + fee;
  }

  /**
   * Recalculates all premiums based on the selected payment frequency.
   * Updates coverages, policy fee, policy premium, and payment frequency code accordingly.
   *
   * @param {string} newFrequencyCode - The payment frequency code to recalculate premiums with.
   * @returns {void}
   */
  recalculatePremiums(newFrequencyCode: string) {
    const ratio = this.getFrequencyRatio(newFrequencyCode);
    // Iterate over all coverages
    for (const coverage of this.coverages()) {
      // Fetch the current base premium
      const basePremium = coverage.premiumWeekly ?? 0; // in cents
      // Update the coverage's premium
      coverage.premiumCurrent = basePremium * ratio;
      coverage.premium!.amount = coverage.premiumCurrent;
    }
    // update the policy fee
    this.policy().fee!.amount = this.policy().feeWeekly! * ratio;
    // update the new selected payment frequency
    this.policy().paymentFrequency = newFrequencyCode;
    this.policy().premium!.amount = this.getGrandTotalPremium();

    //TODO what is localstorage used for?
    // localStorage.setItem('policyData', JSON.stringify(this.dataBreakdown));
  }

  getFrequencyRatio(frequencyCode: string): number {
    return PaymentFrequency[frequencyCode as keyof typeof PaymentFrequency] ?? 1;
  }

  codeVal(coverageCode?: string): string {
    const value = this.coverageCodeVals().find((val: { code: string; val: string }) => val.code === coverageCode);
    return value?.val ?? '';
  }
}
